import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, X, Archive, ArchiveRestore } from "lucide-react";
import { epicengageSupabasev1 } from "../../../../integrations/supabase/client";
import { useMerchant } from "../../../../hooks/useMerchantEngageV1";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { StatusBadge } from "../components/customers/StatusBadge";
import { formatDate } from "../../../../lib/customersEngageV1";
import { useToast } from "../../../../hooks/use-toast";

type Customer = {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  business_name: string | null;
  status: string | null;
  source: string | null;
  joined_at: string | null;
  last_activity_at: string | null;
  archived: boolean | null;
  archived_at: string | null;
};

type EmailSend = {
  id: string;
  status: string | null;
  sent_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
};

const CustomerDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { merchant } = useMerchant();
  const { toast } = useToast();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [sends, setSends] = useState<EmailSend[]>([]);
  const [newTag, setNewTag] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!merchant || !id) return;
    setLoading(true);
    const [{ data: c }, { data: t }, { data: s }] = await Promise.all([
      epicengageSupabasev1.from("customers").select("*").eq("merchant_id", merchant.id).eq("id", id).maybeSingle(),
      epicengageSupabasev1.from("customer_tags").select("tag").eq("merchant_id", merchant.id).eq("customer_id", id).order("created_at"),
      epicengageSupabasev1.from("email_sends").select("id, status, sent_at, opened_at, clicked_at").eq("merchant_id", merchant.id).eq("customer_id", id).order("sent_at", { ascending: false }).limit(50),
    ]);
    setCustomer(c as Customer | null);
    setTags((t ?? []).map((r) => r.tag));
    setSends((s ?? []) as EmailSend[]);
    setLoading(false);
  }, [merchant, id]);

  useEffect(() => { load(); }, [load]);

  const addTag = async () => {
    const tag = newTag.trim().toLowerCase();
    if (!tag || !merchant || !id || tags.includes(tag)) { setNewTag(""); return; }
    const { error } = await epicengageSupabasev1
      .from("customer_tags")
      .upsert({ merchant_id: merchant.id, customer_id: id, tag }, { onConflict: "customer_id,tag", ignoreDuplicates: true });
    if (error) { toast({ title: "Could not add tag", description: error.message, variant: "destructive" }); return; }
    setTags([...tags, tag]);
    setNewTag("");
  };

  const removeTag = async (tag: string) => {
    if (!merchant || !id) return;
    const { error } = await epicengageSupabasev1
      .from("customer_tags")
      .delete()
      .eq("merchant_id", merchant.id)
      .eq("customer_id", id)
      .eq("tag", tag);
    if (error) { toast({ title: "Could not remove tag", description: error.message, variant: "destructive" }); return; }
    setTags(tags.filter((t) => t !== tag));
  };

  const toggleArchive = async () => {
    if (!merchant || !customer) return;
    const next = !customer.archived;
    const { error } = await epicengageSupabasev1
      .from("customers")
      .update({ archived: next, archived_at: next ? new Date().toISOString() : null })
      .eq("merchant_id", merchant.id)
      .eq("id", customer.id);
    if (error) { toast({ title: next ? "Could not archive" : "Could not restore", description: error.message, variant: "destructive" }); return; }
    toast({ title: next ? "Customer archived" : "Customer restored" });
    setCustomer({ ...customer, archived: next, archived_at: next ? new Date().toISOString() : null });
  };

  return (
    <div>
      <div className="px-10 py-10">
        <Link to="/clientpanel/enagev1/customers" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to customers
        </Link>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !customer ? (
          <p className="text-sm text-muted-foreground">Customer not found.</p>
        ) : (
          <>
            <header className="mb-8 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-semibold tracking-tight">{customer.name || customer.email}</h1>
                  {customer.archived && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs border border-border">
                      Archived
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <StatusBadge status={customer.status} />
                  <span className="text-sm text-muted-foreground">{customer.email}</span>
                </div>
              </div>
              <Button variant="outline" onClick={toggleArchive}>
                {customer.archived ? (
                  <><ArchiveRestore className="h-4 w-4" /> Restore</>
                ) : (
                  <><Archive className="h-4 w-4" /> Archive</>
                )}
              </Button>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Field label="Phone" value={customer.phone || "—"} />
              <Field label="Business" value={customer.business_name || "—"} />
              <Field label="Source" value={customer.source ?? "—"} capitalize />
              <Field label="Joined" value={formatDate(customer.joined_at)} />
              <Field label="Last activity" value={formatDate(customer.last_activity_at)} />
            </div>

            <section className="rounded-lg border border-border p-5 mb-6">
              <h2 className="text-sm font-semibold mb-3">Tags</h2>
              <div className="flex flex-wrap gap-1.5 mb-3 min-h-6">
                {tags.length === 0 && <span className="text-xs text-muted-foreground">No tags</span>}
                {tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground rounded-full px-2 py-0.5 text-xs">
                    {t}
                    <button onClick={() => removeTag(t)} className="hover:text-destructive" aria-label={`Remove ${t}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 max-w-sm">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                  placeholder="Add a tag…"
                  maxLength={60}
                />
                <Button variant="outline" onClick={addTag}>Add</Button>
              </div>
            </section>

            <section className="rounded-lg border border-border p-5">
              <h2 className="text-sm font-semibold mb-3">Email history</h2>
              {sends.length === 0 ? (
                <p className="text-sm text-muted-foreground">No emails sent to this customer yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                      <th className="py-2">Sent</th>
                      <th className="py-2">Status</th>
                      <th className="py-2">Opened</th>
                      <th className="py-2">Clicked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sends.map((s) => (
                      <tr key={s.id} className="border-b border-border last:border-0">
                        <td className="py-2">{formatDate(s.sent_at)}</td>
                        <td className="py-2 capitalize">{s.status ?? "—"}</td>
                        <td className="py-2 text-muted-foreground">{s.opened_at ? formatDate(s.opened_at) : "—"}</td>
                        <td className="py-2 text-muted-foreground">{s.clicked_at ? formatDate(s.clicked_at) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, capitalize }: { label: string; value: string; capitalize?: boolean }) => (
  <div className="rounded-lg border border-border p-4">
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`text-sm mt-1 ${capitalize ? "capitalize" : ""}`}>{value}</p>
  </div>
);

export default CustomerDetail;
